<script>
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import OnboardingView from '../OnboardingView.vue';
import EmptyStateMessage from './EmptyStateMessage.vue';

export default {
  components: {
    OnboardingView,
    EmptyStateMessage,
  },
  props: {
    isOnExpandedLayout: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isAdmin } = useAdmin();

    const { accountScopedUrl } = useAccount();

    return {
      isAdmin,
      accountScopedUrl,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      allConversations: 'getAllConversations',
      inboxesList: 'inboxes/getInboxes',
      uiFlags: 'inboxes/getUIFlags',
      loadingChatList: 'getChatListLoadingStatus',
    }),
    newInboxURL() {
      return this.accountScopedUrl('settings/inboxes/new');
    },
    emptyClassName() {
      if (
        !this.inboxesList.length &&
        !this.uiFlags.isFetching &&
        !this.loadingChatList &&
        this.isAdmin
      ) {
        return 'h-full overflow-auto w-full';
      }
      return 'flex-1 min-w-0 px-0 flex flex-col items-center justify-center h-full bg-n-surface-1';
    },
  },
};
</script>

<template>
  <div :class="emptyClassName">
    <!-- Carga sin brincos · P3 (Victor 2026-06-08): spinner dorado discreto, sin
         el texto grande "Cargando conversaciones" que parpadeaba en el centro. -->
    <div
      v-if="uiFlags.isFetching || loadingChatList"
      class="flex items-center justify-center w-full h-full"
    >
      <span
        class="w-7 h-7 rounded-full border-2 border-[#c9a35a]/20 border-t-[#c9a35a] animate-spin"
      />
    </div>
    <!-- No inboxes attached -->
    <div
      v-if="!inboxesList.length && !uiFlags.isFetching && !loadingChatList"
      class="clearfix mx-auto"
    >
      <OnboardingView v-if="isAdmin" />
      <EmptyStateMessage v-else :message="$t('CONVERSATION.NO_INBOX_AGENT')" />
    </div>
    <!-- Show empty state images if not loading -->

    <div
      v-else-if="!uiFlags.isFetching && !loadingChatList"
      class="flex flex-col items-center justify-center h-full w-full"
    >
      <!-- No conversations available -->
      <EmptyStateMessage
        v-if="!allConversations.length"
        :message="$t('CONVERSATION.NO_MESSAGE_1')"
      />
      <!-- Hay chats pero ninguno seleccionado (Victor 2026-05-22): estado vacío
           sutil tipo Kommo · ícono dorado + mensaje en una línea + recordatorio
           de cortesía arriba-izquierda. -->
      <div
        v-else-if="allConversations.length && !currentChat.id"
        class="relative w-full h-full flex flex-col items-center justify-center"
      >
        <div
          class="absolute left-[18px] top-4 flex items-start gap-2 text-left text-[11.5px] leading-[1.5] text-n-slate-11 max-w-[420px]"
        >
          <span class="shrink-0 text-[#d8b15a]">✦</span>
          <span>
            {{ $t('CONVERSATION.EMPTY_REMINDER_PRE')
            }}<b class="font-semibold text-n-slate-12">{{
              $t('CONVERSATION.EMPTY_REMINDER_BOLD1')
            }}</b>{{ $t('CONVERSATION.EMPTY_REMINDER_MID') }}
            <span class="block">
              <b class="font-semibold text-n-slate-12">{{
                $t('CONVERSATION.EMPTY_REMINDER_BOLD2')
              }}</b>
            </span>
          </span>
        </div>
        <div class="flex flex-col items-center text-center">
          <div
            class="w-[38px] h-[38px] mb-3.5 rounded-[11px] flex items-center justify-center bg-[linear-gradient(135deg,#e9cd83,#c79a3f)] shadow-[0_8px_20px_rgba(216,177,90,0.25)]"
          >
            <svg viewBox="0 0 24 24" fill="#0a0a0b" class="w-5 h-5">
              <path
                d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM7 9h10v2H7zm0 4h7v2H7z"
              />
            </svg>
          </div>
          <div
            class="text-[13.5px] font-semibold text-n-slate-12 whitespace-nowrap"
          >
            {{ $t('CONVERSATION.EMPTY_SELECT_TITLE') }}
          </div>
          <div class="mt-1.5 text-[11.5px] text-n-slate-11 whitespace-nowrap">
            {{ $t('CONVERSATION.EMPTY_RESOLVE_PREFIX') }}
            <span class="font-medium text-[#25d366]">{{
              $t('CONVERSATION.EMPTY_RESOLVE_HL')
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
